import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { legacyApi, RelatedOpenSaleOrder } from '../../../services/legacyApi';
import { toast } from 'sonner';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../../components/forms/formValidationUtils';
import { useSettings } from '../../../context/SettingsContext';
import { RelatedOpenSaleOrdersNotice } from '../components/RelatedOpenSaleOrdersNotice';

type OrderLine = {
    part_name: string;
    item_id?: number;
    quantity: number;
    unit_price: number;
    category?: string;
    color_finish?: string;
};

export const LegacySaleOrderCreate: React.FC = () => {
    const navigate = useNavigate();
    const { getSetting } = useSettings();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState<any[]>([]);
    const [relatedOpenOrders, setRelatedOpenOrders] = useState<RelatedOpenSaleOrder[]>([]);

    const [customerId, setCustomerId] = useState<number>(0);
    const [soDate, setSoDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [customerPoNo, setCustomerPoNo] = useState<string>('');
    const [poIssueDate, setPoIssueDate] = useState<string>('');
    const [remarks, setRemarks] = useState<string>('');
    const [poRemarks, setPoRemarks] = useState<string>('');
    const [vatPercent, setVatPercent] = useState<number>(5);
    const [discountPercent, setDiscountPercent] = useState<number>(0);
    const [flowMode, setFlowMode] = useState<'SIMPLE' | 'PRODUCTION'>('PRODUCTION');
    const [lines, setLines] = useState<OrderLine[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const selectiveFlowEnabled = ['1', 'true', 'yes', 'on', 'enabled'].includes(
        String(getSetting('sales_selective_flow_enabled') || '').trim().toLowerCase()
    );
    const simpleModeDefault = ['1', 'true', 'yes', 'on', 'enabled'].includes(
        String(getSetting('sales_simple_mode_enabled') || '').trim().toLowerCase()
    );

    useEffect(() => {
        setFlowMode(simpleModeDefault ? 'SIMPLE' : 'PRODUCTION');
    }, [simpleModeDefault]);

    useEffect(() => {
        const load = async () => {
            try {
                const customerRows = await legacyApi.customers.list();
                setCustomers(Array.isArray(customerRows) ? customerRows : []);
                if (customerRows?.length) {
                    const firstId = Number(customerRows[0]?.customer_id || 0);
                    if (firstId) setCustomerId(firstId);
                }
            } catch (error: any) {
                toast.error(error?.message || 'Failed to load order setup data');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!customerId) {
            setRelatedOpenOrders([]);
            return () => {
                cancelled = true;
            };
        }
        legacyApi.sales.listRelatedOpenOrders(Number(customerId))
            .then((rows) => {
                if (!cancelled) setRelatedOpenOrders(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {
                if (!cancelled) setRelatedOpenOrders([]);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId]);

    const addLine = () => {
        setLines((prev) => [
            ...prev,
            {
                part_name: '',
                quantity: 1,
                unit_price: 0,
                category: 'Finished Goods',
                color_finish: '',
            },
        ]);
    };

    const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

    const updateLine = (idx: number, patch: Partial<OrderLine>) => {
        setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
    };

    const subTotal = useMemo(
        () => lines.reduce((sum, line) => sum + Number(line.quantity || 0) * Number(line.unit_price || 0), 0),
        [lines]
    );
    const discountAmount = useMemo(() => (Number(discountPercent || 0) / 100) * subTotal, [discountPercent, subTotal]);
    const amountAfterDiscount = useMemo(() => subTotal - discountAmount, [discountAmount, subTotal]);
    const vatAmount = useMemo(() => (Number(vatPercent || 0) / 100) * amountAfterDiscount, [vatPercent, amountAfterDiscount]);
    const grandTotal = useMemo(() => amountAfterDiscount + vatAmount, [amountAfterDiscount, vatAmount]);

    const submit = async () => {
        const headerValidation = validateRequiredFields([
            { key: 'customer_id', label: 'Customer', value: customerId, required: true },
        ]);
        const nextErrors = { ...headerValidation.errorsByField } as Record<string, string>;
        if (!lines.length) {
            nextErrors.lines = 'Add at least one line item';
        }
        lines.forEach((line, idx) => {
            if (!String(line.part_name || '').trim()) nextErrors[`line_${idx}_part_name`] = 'Part Name is required';
            if (Number(line.quantity || 0) <= 0) nextErrors[`line_${idx}_quantity`] = 'Quantity must be greater than zero';
            if (Number(line.unit_price || 0) < 0) nextErrors[`line_${idx}_unit_price`] = 'Unit price cannot be negative';
        });
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(Object.keys(nextErrors)[0]);
            return;
        }
        setFieldErrors({});
        if (Number(vatPercent) < 0 || Number(vatPercent) > 100) {
            toast.error('VAT must be between 0 and 100');
            return;
        }
        if (Number(discountPercent) < 0 || Number(discountPercent) > 100) {
            toast.error('Discount must be between 0 and 100');
            return;
        }

        setSubmitting(true);
        try {
            const created = await legacyApi.sales.createOrder({
                customer_id: customerId,
                customer_po_no: customerPoNo || undefined,
                so_date: soDate || undefined,
                po_issue_date: poIssueDate || undefined,
                so_status_id: 1,
                so_remarks: remarks || undefined,
                po_remarks: poRemarks || undefined,
                vat_percent: Number(vatPercent || 0),
                discount_percent: Number(discountPercent || 0),
                flow_mode: selectiveFlowEnabled ? flowMode : undefined,
                lines: lines.map((line) => ({
                    item_id: line.item_id ? Number(line.item_id) : undefined,
                    part_name: String(line.part_name || '').trim(),
                    quantity: Number(line.quantity),
                    unit_price: Number(line.unit_price),
                    category: line.category || 'Finished Goods',
                    color_finish: line.color_finish || undefined,
                })),
            });
            toast.success('Sale order created');
            navigate(`/legacy/orders/${created.so_id}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create sale order');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-6">Loading order setup...</div>;

    const selectedCustomerName = customers.find((customer) => Number(customer.customer_id) === Number(customerId))?.customer_name;

    return (
        <div className="space-y-6 p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" onClick={() => navigate('/legacy/orders')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Create Sale Order Draft</h1>
                        <p className="text-muted-foreground">Direct sale orders are created in draft and can be edited before confirmation.</p>
                    </div>
                </div>
                <Button onClick={submit} disabled={submitting}>
                    <Save className="h-4 w-4 mr-2" /> {submitting ? 'Saving...' : 'Create Draft'}
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Order Header</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <RequiredLabel required htmlFor="customer_id">Customer</RequiredLabel>
                        <select
                            id="customer_id"
                            name="customer_id"
                            aria-invalid={!!fieldErrors.customer_id}
                            aria-required="true"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={customerId || ''}
                            onChange={(e) => setCustomerId(Number(e.target.value || 0))}
                        >
                            <option value="">Select customer...</option>
                            {customers.map((customer) => (
                                <option key={customer.customer_id} value={customer.customer_id}>
                                    {customer.customer_name}
                                </option>
                            ))}
                        </select>
                        <FieldErrorText id="customer_id_error" message={fieldErrors.customer_id} />
                    </div>
                    <div className="space-y-2">
                        <Label>SO Date</Label>
                        <Input type="date" value={soDate} onChange={(e) => setSoDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Customer PO No</Label>
                        <Input value={customerPoNo} onChange={(e) => setCustomerPoNo(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>PO Issue Date</Label>
                        <Input type="date" value={poIssueDate} onChange={(e) => setPoIssueDate(e.target.value)} />
                    </div>
                    {selectiveFlowEnabled ? (
                        <div className="space-y-2 md:col-span-2">
                            <Label>Flow Mode</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={flowMode}
                                onChange={(e) => setFlowMode((e.target.value || 'PRODUCTION') as 'SIMPLE' | 'PRODUCTION')}
                            >
                                <option value="PRODUCTION">Production</option>
                                <option value="SIMPLE">Simple</option>
                            </select>
                            <p className="text-xs text-muted-foreground">
                                `Simple` bypasses production and uses provisional stock. `Production` keeps the normal work-order and inventory flow.
                            </p>
                        </div>
                    ) : null}
                    <div className="space-y-2 md:col-span-2">
                        <Label>SO Remarks</Label>
                        <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <Label>PO Remarks</Label>
                        <Input value={poRemarks} onChange={(e) => setPoRemarks(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>VAT (%)</Label>
                        <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={vatPercent}
                            onChange={(e) => setVatPercent(Number(e.target.value || 0))}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Discount (%)</Label>
                        <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={discountPercent}
                            onChange={(e) => setDiscountPercent(Number(e.target.value || 0))}
                        />
                    </div>
                </CardContent>
            </Card>

            <RelatedOpenSaleOrdersNotice orders={relatedOpenOrders} customerName={selectedCustomerName} />

            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Order Lines</CardTitle>
                    <Button type="button" variant="outline" onClick={addLine}>
                        <Plus className="h-4 w-4 mr-2" /> Add Line
                    </Button>
                </CardHeader>
                <CardContent>
                    <div className="border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr>
                                    <th className="p-3 text-left">Part Name</th>
                                    <th className="p-3 text-right w-28">Qty</th>
                                    <th className="p-3 text-right w-32">Unit Price</th>
                                    <th className="p-3 text-right w-32">Line Total</th>
                                    <th className="p-3 text-center w-16">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {lines.length === 0 ? (
                                    <tr>
                                        <td className="p-6 text-center text-muted-foreground" colSpan={5}>
                                            Add part lines to start creating the order.
                                        </td>
                                    </tr>
                                ) : (
                                    lines.map((line, idx) => (
                                        <tr key={`line-${idx}`}>
                                            <td className="p-2">
                                                <Input
                                                    id={`line_${idx}_part_name`}
                                                    name={`line_${idx}_part_name`}
                                                    aria-invalid={!!fieldErrors[`line_${idx}_part_name`]}
                                                    aria-required="true"
                                                    value={line.part_name}
                                                    placeholder="Enter part name / description"
                                                    onChange={(e) => updateLine(idx, { part_name: e.target.value })}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <Input
                                                    id={`line_${idx}_quantity`}
                                                    name={`line_${idx}_quantity`}
                                                    aria-invalid={!!fieldErrors[`line_${idx}_quantity`]}
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    value={line.quantity}
                                                    className="text-right"
                                                    onChange={(e) => updateLine(idx, { quantity: Number(e.target.value || 0) })}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <Input
                                                    id={`line_${idx}_unit_price`}
                                                    name={`line_${idx}_unit_price`}
                                                    aria-invalid={!!fieldErrors[`line_${idx}_unit_price`]}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={line.unit_price}
                                                    className="text-right"
                                                    onChange={(e) => updateLine(idx, { unit_price: Number(e.target.value || 0) })}
                                                />
                                            </td>
                                            <td className="p-2 text-right font-medium">
                                                {(Number(line.quantity || 0) * Number(line.unit_price || 0)).toFixed(2)}
                                            </td>
                                            <td className="p-2 text-center">
                                                <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {fieldErrors.lines && (
                                <tbody>
                                    <tr>
                                        <td colSpan={5} className="px-3 pb-2">
                                            <FieldErrorText message={fieldErrors.lines} />
                                        </td>
                                    </tr>
                                </tbody>
                            )}
                            <tfoot className="bg-muted/40">
                                <tr>
                                    <td colSpan={3} className="p-3 text-right font-semibold">Subtotal</td>
                                    <td className="p-3 text-right font-semibold">{subTotal.toFixed(2)}</td>
                                    <td />
                                </tr>
                                <tr>
                                    <td colSpan={3} className="p-2 text-right text-sm text-muted-foreground">Discount ({discountPercent || 0}%)</td>
                                    <td className="p-2 text-right text-sm text-red-600">-{discountAmount.toFixed(2)}</td>
                                    <td />
                                </tr>
                                <tr>
                                    <td colSpan={3} className="p-2 text-right text-sm text-muted-foreground">VAT ({vatPercent || 0}%)</td>
                                    <td className="p-2 text-right text-sm text-green-600">+{vatAmount.toFixed(2)}</td>
                                    <td />
                                </tr>
                                <tr>
                                    <td colSpan={3} className="p-3 text-right font-semibold">Grand Total</td>
                                    <td className="p-3 text-right font-semibold">{grandTotal.toFixed(2)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
